/**
 * ActiveRuns.tsx — Automation / Active Runs page (Closr style)
 *
 * P1: colors/sizes flow from src/theme/tokens.ts; sizes sit on the P1 scale.
 */
import { ChevronRight } from 'lucide-react'
import { useState } from 'react'
import { SectionTitle, StatusBadge, LoadingState, ErrorState, EmptyState } from '../DashboardShared'
import { useAsync, fetchRuns } from '../api'

function fmtAgo(iso: string) {
  const ms = Date.now() - new Date(iso).getTime()
  const m = Math.floor(ms / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  return `${Math.floor(m / 60)}h ago`
}

// Statuses considered "active" (live) for this view.
const ACTIVE_STATUSES = ['running', 'waiting', 'queued', 'admitted']

export function ActiveRuns() {
  const [filter, setFilter] = useState<'all' | 'running' | 'waiting' | 'queued'>('all')
  const { data, loading, error } = useAsync(() => fetchRuns(50), [])

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} />

  const activeRuns = (data ?? []).filter(r => ACTIVE_STATUSES.includes(r.status))
  const filtered = activeRuns.filter(r => filter === 'all' || r.status === filter)

  return (
    <div className="page-enter flex flex-col gap-5">
      {/* The old Refresh button was DEAD (no handler) and is gone -- a restyle
          must not re-ship a control that does nothing (the Contacts-pagination
          precedent). Data still refreshes on navigation. */}
      <SectionTitle
        title="Active Runs"
        subtitle="Live view of running and recently queued runs"
      />

      <div className="k-card">
        <div className="flex items-center gap-2 px-5 py-3 border-b border-edge-row">
          {(['all', 'running', 'waiting', 'queued'] as const).map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-2 rounded-full text-12 font-medium transition-colors capitalize ${filter === f ? 'bg-ink-primary text-ink-on-accent' : 'text-ink-secondary hover:bg-surface-nav-hover'}`}
            >
              {f}
            </button>
          ))}
          <span className="ml-auto text-11 text-ink-muted">{filtered.length} run{filtered.length !== 1 ? 's' : ''}</span>
        </div>

        {filtered.length === 0 ? (
          /* The slice-A Schedules fix, applied here (the flagged symptom):
             bare column headers over nothing said nothing. */
          <EmptyState
            message="No active runs"
            hint={filter === 'all'
              ? 'Runs appear here while they are running, waiting, or queued.'
              : 'Nothing matches this status filter.'}
            actionLabel={filter !== 'all' ? 'Show all' : undefined}
            onAction={() => setFilter('all')}
          />
        ) : (
        <table className="closr-table dash-table w-full">
          <thead>
            <tr>
              <th className="text-left">Run ID</th>
              <th className="text-left">Agent</th>
              <th className="text-left">Status</th>
              <th className="text-left">Started / held</th>
              <th className="text-left">Duration</th>
              <th className="text-left">Current Step</th>
              <th className="text-left">Trigger</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(run => (
              <tr key={run.id} className="cursor-pointer">
                <td className="font-mono text-12 text-ink-secondary">{run.id}</td>
                <td className="font-medium text-ink-primary">{run.agentName}</td>
                <td><StatusBadge status={run.held ? 'held' : run.status} /></td>
                <td className="text-12 text-ink-muted">{fmtAgo(run.startedAt)}</td>
                <td className="font-mono text-12">{run.duration}</td>
                <td className="text-12 text-ink-secondary max-w-[180px] truncate">{run.currentStep}</td>
                <td><span className="k-badge bg-surface-panel text-ink-secondary">{run.trigger.toUpperCase()}</span></td>
                <td className="text-right"><ChevronRight size={14} className="text-ink-disabled inline" /></td>
              </tr>
            ))}
          </tbody>
        </table>
        )}
      </div>
    </div>
  )
}
